import React, { useMemo } from "react";
import { format, parseISO, isToday, isTomorrow } from "date-fns";
import { es } from "date-fns/locale";
import { CalendarDays, Clock, MapPin, Trash2, Edit2, UserRound } from "lucide-react";

import { statusMeta, DEFAULT_REASON_COLOR } from "./appointmentUtils";

const dayHeading = (date) => {
  if (isToday(date)) return "Hoy";
  if (isTomorrow(date)) return "Mañana";
  return format(date, "EEEE d 'de' MMMM", { locale: es });
};

const ListView = ({ appointments, onSelectAppointment, onDelete, canDelete }) => {
  const groups = useMemo(() => {
    const map = new Map();
    [...appointments]
      .sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt))
      .forEach((appointment) => {
        const key = format(parseISO(appointment.scheduledAt), "yyyy-MM-dd");
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(appointment);
      });
    return Array.from(map.entries());
  }, [appointments]);

  if (groups.length === 0) {
    return (
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 py-16 text-center">
        <CalendarDays className="w-10 h-10 text-slate-200 dark:text-slate-700 mx-auto mb-3" />
        <p className="text-base font-semibold text-slate-500 dark:text-slate-400">
          No hay citas en este período
        </p>
        <p className="text-sm text-slate-400 mt-1">
          Agenda una cita para que el contacto reciba su recordatorio automático
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {groups.map(([key, dayAppointments]) => (
        <div key={key}>
          <p className="mb-2 pl-1 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
            {dayHeading(parseISO(`${key}T00:00:00`))}
          </p>
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800 overflow-hidden">
            {dayAppointments.map((appointment) => {
              const meta = statusMeta(appointment.status);
              const date = parseISO(appointment.scheduledAt);
              const color = appointment.reason?.color || DEFAULT_REASON_COLOR;

              return (
                <div
                  key={appointment.id}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors"
                >
                  <span
                    className="w-1 self-stretch rounded-full flex-shrink-0"
                    style={{ backgroundColor: color }}
                  />
                  <div className="w-16 flex-shrink-0">
                    <p className="text-sm font-bold text-slate-900 dark:text-slate-100 tabular-nums">
                      {format(date, "HH:mm")}
                    </p>
                    <p className="text-[10px] text-slate-400 flex items-center gap-0.5">
                      <Clock className="w-2.5 h-2.5" />
                      {appointment.durationMinutes} min
                    </p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-sm font-medium text-slate-900 dark:text-slate-100 truncate ${
                        appointment.status === "cancelled" ? "line-through opacity-60" : ""
                      }`}
                    >
                      {appointment.contact?.name}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate flex items-center gap-2 mt-0.5">
                      {appointment.reason?.name && <span>{appointment.reason.name}</span>}
                      {appointment.professional?.name && (
                        <span className="flex items-center gap-0.5">
                          <UserRound className="w-3 h-3" />
                          {appointment.professional.name}
                        </span>
                      )}
                      {appointment.location && (
                        <span className="flex items-center gap-0.5">
                          <MapPin className="w-3 h-3" />
                          {appointment.location}
                        </span>
                      )}
                    </p>
                  </div>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-bold border flex-shrink-0 ${meta.badge}`}
                  >
                    {meta.label}
                  </span>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => onSelectAppointment(appointment)}
                      title="Editar"
                      className="p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    {canDelete && (
                      <button
                        onClick={() => onDelete(appointment)}
                        title="Eliminar"
                        className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ListView;
